// University of Pennsylvania: public college page (#2596), official facts only.
//
// Every figure below was read on 2026-09-30 from the source it names:
//  - Penn's Common Data Set 2025-26 (dated 23 June 2026), downloaded from the
//    "2025-26 Penn Common Data Set (PDF)" link on Institutional Research and
//    Analysis's CDS page (the file itself sits on Penn's Box,
//    https://upenn.box.com/s/woo7d85yxpol6j9e2o121aucjc6dc1k5; the page links
//    it). C1 applicants/admits/enrollees for Fall 2025 with the residency
//    table; C8 test policy for Fall 2027; C21 early decision counts; G1/G5
//    2026-2027 costs, which match SRFS's $99,082 on-campus budget.
//  - admissions.upenn.edu (first-year applicants, testing).
//  - College Scorecard, school id 215062 (net price by family income): the
//    API response the collegescorecard.ed.gov school page loads (the
//    DEMO_KEY call was rate-limited). The $0 to $30,000 band is negative in
//    the Department's own data.
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Penn Common Data Set 2025-2026',
        url: 'https://ira.upenn.edu/penn-numbers/common-data-set',
        read: '2026-09-30',
    },
    firstYear: {
        label: 'Penn Admissions, First-Year Applicants',
        url: 'https://admissions.upenn.edu/how-to-apply/first-year-applicants',
        read: '2026-09-30',
    },
    tests: {
        label: 'Penn Admissions, Testing',
        url: 'https://admissions.upenn.edu/how-to-apply/preparing-your-application/testing',
        read: '2026-09-30',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?215062-University-of-Pennsylvania',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-pennsylvania',
    kbId: 'university_of_pennsylvania',
    name: 'University of Pennsylvania',
    shortName: 'Penn',
    aliases: ['UPenn'],
    location: 'Philadelphia, PA',
    control: 'Private',
    lastChecked: '2026-09-30',
    sources: SOURCES,
    // From the KB's sourced branding (Penn web branding); decoration only.
    branding: {
        primary: '#990000',
        secondary: '#011F5B',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_pennsylvania_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 72544,
        admitted: 3570,
        enrolled: 2420,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 5411, admitted: 451 },
            { group: 'Out-of-state', applied: 49251, admitted: 2678 },
            { group: 'International', applied: 17882, admitted: 441 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'You apply to one of Penn\'s four undergraduate schools or a coordinated dual-degree program, but Penn\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        rows: [
            { plan: 'Early Decision', applied: 9503, admitted: 1266 },
        ],
        note: 'Early decision counts for the Fall 2025 entering class, as the Common Data Set reports them. They are part of the totals above.',
        source: 'cds',
        where: 'section C21',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 5' },
        rows: [
            { plan: 'Early Decision', deadline: 'November 1', decision: 'December', binding: true },
            { plan: 'Regular Decision', deadline: 'January 5', decision: 'April', binding: false },
        ],
        note: 'Penn tells you the exact date and time of its decision release at least one week ahead.',
        source: 'firstYear',
        binding: 'Early Decision is binding: if you are admitted, you are required to accept Penn\'s offer of admission and enroll at Penn.',
        bindingSource: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-required',
        blind: false,
        line: 'Penn requires SAT or ACT scores. If you have not taken either test and face a hardship, such as no test center, cost, a natural disaster or civil unrest, you can submit a testing waiver instead. If you have taken a test, you must submit the scores.',
        source: 'tests',
        cdsLine: 'The Common Data Set marks the SAT or ACT as required for fall 2027 applicants.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-09-30',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 65670 },
            { item: 'Required fees', amount: 8308 },
            { item: 'Housing (on campus)', amount: 13644 },
            { item: 'Food (on-campus meal plan)', amount: 6960 },
            { item: 'Books and supplies', amount: 1412 },
            { item: 'Transportation', amount: 1080 },
            { item: 'Other expenses', amount: 2008 },
        ],
        total: 99082,
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 29577,
            byIncome: [
                { income: '$0 to $30,000', amount: null },
                { income: '$30,001 to $48,000', amount: 234 },
                { income: '$48,001 to $75,000', amount: 12421 },
                { income: '$75,001 to $110,000', amount: 25263 },
                { income: 'Over $110,000', amount: 61455 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. The Department publishes no figure for the $0 to $30,000 band. Your own price comes from Penn\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
